import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Apple,
    ContentCopy,
    Download,
    Extension,
    LockOutlined,
    Verified,
} from '@mui/icons-material';
import { WindowsLogoIcon } from '../../components/icons/PlatformIcons';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import useAlertModal from '../../components/AlertModal';

const FARM_LINK_DOWNLOAD_WINDOWS = 'https://acompanha.s3.us-east-1.amazonaws.com/Acompanha+3D+Farm+Link+-+Windows.zip';
const FARM_LINK_DOWNLOAD_MAC = 'https://acompanha.s3.us-east-1.amazonaws.com/Acompanha+3D+Farm+Link.zip';

function IntegrationsPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [planName, setPlanName] = useState('');
    const [integrationKey, setIntegrationKey] = useState('');
    const { showAlert } = useAlertModal();

    useEffect(() => {
        api.get('me/integrations')
            .then((response) => {
                const payload = response?.data?.data;
                if (!payload) {
                    setDidNotLoad(true);
                    return;
                }
                setHasProAccess(Boolean(payload.has_pro_access));
                setPlanName(payload.plan_name || '');
                setIntegrationKey(payload.integration_key || '');
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleCopyKey = async () => {
        if (!integrationKey) {
            return;
        }

        try {
            await navigator.clipboard.writeText(integrationKey);
            showAlert({
                title: 'Copiado!',
                message: 'Sua chave de integração foi copiada para a área de transferência.',
                iconType: 'success',
            });
        } catch {
            showAlert({
                title: 'Não foi possível copiar',
                message: 'Selecione a chave manualmente e use Ctrl+C (ou Cmd+C).',
                iconType: 'warning',
            });
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <Box padding={2} maxWidth={920} mx="auto">
            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <Extension color="primary" sx={{ fontSize: 36 }} />
                <Typography variant="h4" component="h1">
                    Integrações
                </Typography>
            </Stack>

            <Typography variant="body1" color="text.secondary" mb={3}>
                Conecte suas ferramentas e automatize fluxos com o Acompanha 3D.
            </Typography>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                Nesta área você encontra as integrações disponíveis para a sua conta.
                Use a chave de integração para conectar impressoras, serviços e outros recursos compatíveis.
                Mantenha sua chave em segurança — ela identifica sua conta nos sistemas externos.
            </Alert>

            {hasProAccess ? (
                <Stack spacing={3}>
                    <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                        <Stack spacing={3}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                    icon={<Verified />}
                                    label={`Plano ${planName || 'Pro'} ativo`}
                                    color="success"
                                    variant="outlined"
                                />
                            </Stack>

                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Chave para conectar ao Acompanha 3D Farm Link
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={1.5}>
                                    Copie a chave abaixo e insira-a na aplicação{' '}
                                    <strong>Acompanha 3D Farm Link Desktop</strong> para sincronizar as
                                    impressoras da sua farm e habilitar o report automático de impressões,
                                    além de outros recursos incluídos no seu plano.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Por segurança, esta chave será desativada automaticamente em caso de
                                    cancelamento do plano ou de atraso superior a 7 dias na mensalidade.
                                </Typography>

                                <TextField
                                    fullWidth
                                    value={integrationKey}
                                    InputProps={{
                                        readOnly: true,
                                        sx: { fontFamily: 'monospace', fontSize: '0.95rem' },
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleCopyKey}
                                                    edge="end"
                                                    aria-label="Copiar chave de integração"
                                                    color="primary"
                                                >
                                                    <ContentCopy />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<ContentCopy />}
                                    onClick={handleCopyKey}
                                    sx={{ mt: 2 }}
                                >
                                    Copiar chave
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>

                    <Paper elevation={3} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
                        <Stack spacing={2.5}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Download color="primary" />
                                <Typography variant="h6" component="h2">
                                    Download do Acompanha 3D Farm Link Desktop
                                </Typography>
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                Instale o aplicativo no computador que gerencia sua farm de impressoras.
                                Após a instalação, abra o Farm Link, insira sua chave de integração e
                                sincronize suas impressoras — com tudo configurado, os dados da farm
                                ficam prontos para serem atualizados em tempo real no Acompanha 3D.
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                useFlexGap
                            >
                                <Button
                                    component="a"
                                    href={FARM_LINK_DOWNLOAD_WINDOWS}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="outlined"
                                    size="large"
                                    startIcon={<WindowsLogoIcon />}
                                    sx={{ flex: { sm: 1 }, py: 1.25 }}
                                >
                                    Baixar para Windows
                                </Button>
                                <Button
                                    component="a"
                                    href={FARM_LINK_DOWNLOAD_MAC}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="outlined"
                                    size="large"
                                    startIcon={<Apple />}
                                    sx={{ flex: { sm: 1 }, py: 1.25 }}
                                >
                                    Baixar para Mac
                                </Button>
                            </Stack>

                            <Typography variant="caption" color="text.secondary">
                                Requisitos: Windows 10 ou superior · macOS 12 (Monterey) ou superior
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            ) : (
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        textAlign: 'center',
                        background: (theme) => (
                            theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, rgba(25,118,210,0.12) 0%, rgba(0,0,0,0) 100%)'
                                : 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 100%)'
                        ),
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'action.hover',
                        }}
                    >
                        <LockOutlined color="primary" sx={{ fontSize: 36 }} />
                    </Box>

                    <Typography variant="h5" gutterBottom>
                        Integrações disponíveis no plano Pro
                    </Typography>

                    <Typography variant="body1" color="text.secondary" mb={1} maxWidth={560} mx="auto">
                        Para acessar recursos de integração e obter sua chave pessoal, é necessário ser assinante
                        do plano <strong>Pro</strong> com assinatura ativa.
                    </Typography>

                    {planName && planName !== 'Free' && planName !== 'free' && (
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Plano atual: {planName}
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" maxWidth={560} mx="auto">
                        Assim que sua assinatura Pro estiver ativa, volte a esta página para visualizar e copiar
                        sua chave de integração.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}

export default IntegrationsPage;
